import { createReducer } from 'redux-starter-kit';
import {
  editPackage, cancelEditPackage, togglePackageActive, cancelTogglePackage,
  updatePackage, savePackage, fetchAllPackages, fetchAllPurchaseSummaries,
  fetchPurchaseHistoryForPackage, putPackagePurchaseRange, fetchAllUsageSummaries,
  fetchMyUsageSummaries, putCreditHistoryRange,
} from './packageUiActions';
import { selectRequest } from '../../../domain/requests/RequestReducer';

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the package being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.packages.editState;

export const selectToggleStatus = state => state.via.ui.packages.activatingPackages;

/**
 * Selects a map of package-id to the date range that purchases should be displayed for it on the PackagePurchases screen.
 */
export const selectPurchaseRanges = state => state.via.ui.packages.purchaseRanges;

/**
 * Selects a map of package-id to the date range that purchases should be displayed for it on the PackagePurchases screen.
 */
export const selectCreditRanges = state => state.via.ui.packages.creditRanges;

export const selectSubmitPackageRequest = (state) => {
  const requestId = state.via.ui.packages.editState.submitPackageRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectFetchPackagesRequest = (state) => {
  const requestId = state.via.ui.packages.fetchPackagesRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectFetchPurchaseSummariesRequest = (state) => {
  const requestId = state.via.ui.packages.fetchSummariesRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};


export const selectFetchUsageSummariesRequest = (state) => {
  const requestId = state.via.ui.packages.fetchUsageSummariesRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectFetchMyUsageSummariesRequest = (state) => {
  const requestId = state.via.ui.packages.fetchMyUsageSummariesRequestId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

export const selectRequestPurchaseHistoryForPackageId = (state) => {
  const requestId = state.via.ui.packages.requestPurchaseHistoryForPackageId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

const initialEditingState = {
  isEditing: false,
  code: null,
  fetchPackagesRequestId: null,
  validationMessages: ({
    title: '',
    messages: [],
    visible: false,
  }),
};

const initialPurchasesState = {
  // Map of package id to ISO strings e.g
  // 2: { fromDateString: '2019-09-01T15:10:12Z', toDateString: '2019-10-01T15:10:12' },
};

const initialCreditRanges = {
  // Map of package id to ISO strings e.g
  // 2: { fromDateString: '2019-09-01T15:10:12Z', toDateString: '2019-10-01T15:10:12' },
};

const packagesInitialState = {
  editState: initialEditingState,
  purchaseRanges: initialPurchasesState,
  creditRanges: initialCreditRanges,
  activatingPackages: [],
  submitPackageRequestId: null,
  fetchSummariesRequestId: null,
  requestPurchaseHistoryForPackageId: null,
  fetchUsageSummariesRequestId: null,
  fetchMyUsageSummariesRequestId: null,
};

/**
 * A reducer for handling the UI state changes for the Manage Users Configuration screen.
 */
const packageUiReducer = createReducer(packagesInitialState, {
  // A package is being edited
  [editPackage]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      code: action.payload,
    },
  }),

  [cancelEditPackage]: state => ({
    ...state,
    editState: {
      ...initialEditingState,
    },
  }),

  [updatePackage]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      submitPackageRequestId: action.payload,
    },
  }),

  [savePackage]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      submitPackageRequestId: action.payload,
    },
  }),

  [fetchAllPackages]: (state, action) => ({
    ...state,
    fetchPackagesRequestId: action.payload,
  }),
  [fetchAllPurchaseSummaries]: (state, action) => ({
    ...state,
    fetchSummariesRequestId: action.payload,
  }),

  [togglePackageActive]: (state, action) => ({
    ...state,
    activatingPackages: [...state.activatingPackages, action.payload],
  }),

  [cancelTogglePackage]: (state, action) => ({
    ...state,
    activatingPackages: state.activatingPackages.filter(code => code !== action.payload),
  }),

  [fetchPurchaseHistoryForPackage]: (state, action) => ({
    ...state,
    requestPurchaseHistoryForPackageId: action.payload,
  }),

  [fetchAllUsageSummaries]: (state, action) => ({
    ...state,
    fetchUsageSummariesRequestId: action.payload,
  }),

  [fetchMyUsageSummaries]: (state, action) => ({
    ...state,
    fetchMyUsageSummariesRequestId: action.payload,
  }),

  [putPackagePurchaseRange]: (state, action) => {
    const newPackage = action.payload;
    return ({
      ...state,
      purchaseRanges: {
        ...state.purchaseRanges,
        ...newPackage,
      },
    });
  },

  [putCreditHistoryRange]: (state, action) => {
    const newCreditRange = action.payload;
    return ({
      ...state,
      creditRanges: {
        ...state.creditRanges,
        ...newCreditRange,
      },
    });
  },
});

export default packageUiReducer;
